import { useState, useEffect } from 'react';
import { Reveal } from './animations';
import AppNav from './AppNav';
import { useProfile } from './ProfileContext';
import { supabase } from './supabaseClient';
import { normalizeBranch } from './branch';
import './NetworkPage.css';

const BRANCH_OPTIONS    = ['All Branches','U.S. Army','U.S. Marine Corps','U.S. Navy','U.S. Air Force','U.S. Space Force','U.S. Coast Guard'];
const SKILL_OPTIONS     = ['All Skills','Cybersecurity','Leadership','Logistics','Intelligence','Aviation','Finance','Engineering','Medical','Law Enforcement'];
const TABS              = ['Browse Veterans','Mentor Matching'];

const AVATAR_COLORS = [
  { color: '#63b3ed', bg: 'rgba(99,179,237,0.15)'  },
  { color: '#C9A84C', bg: 'rgba(201,168,76,0.15)'  },
  { color: '#a78bfa', bg: 'rgba(167,139,250,0.15)' },
  { color: '#f6ad55', bg: 'rgba(246,173,85,0.15)'  },
  { color: '#68d391', bg: 'rgba(104,211,145,0.15)' },
  { color: '#fc8181', bg: 'rgba(252,129,129,0.15)' },
];

function getAvatarColor(id) {
  const index = id ? id.charCodeAt(0) % AVATAR_COLORS.length : 0;
  return AVATAR_COLORS[index];
}

export default function NetworkPage() {
  const { profile } = useProfile();
  const [currentUserId, setCurrentUserId] = useState(null);
  const [activeTab,     setActiveTab]     = useState('Browse Veterans');
  const [search,        setSearch]        = useState('');
  const [branchFilter,  setBranchFilter]  = useState('All Branches');
  const [skillFilter,   setSkillFilter]   = useState('All Skills');

  // Real data
  const [members,      setMembers]      = useState([]);
  const [follows,      setFollows]      = useState({}); // { memberId: true }
  const [connections,  setConnections]  = useState({}); // { memberId: 'pending'|'connected' }
  const [pendingIn,    setPendingIn]    = useState([]); // incoming requests
  const [loadingMembers, setLoadingMembers] = useState(true);

  // ── Load current user + members + relationships ───────────────
  useEffect(() => {
    async function load() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const uid = session.user.id;
      setCurrentUserId(uid);

      // Load qualified members. profiles_public masks fields per-row based
      // on that member's own privacy settings -- branch/rank/occupation may
      // be null here even though the member filled them in, if they chose
      // not to share military info with fellow members. wizard_complete is
      // the completeness signal (not branch/occupation_code, which are now
      // legitimately nullable for privacy reasons rather than incompleteness).
      const { data: membersData } = await supabase
        .from('profiles_public')
        .select(
          'id, first_name, last_name, avatar_url, branch, rank, location, ' +
          'career_goal, clearance, veteran_verified, occupation_code, occupation_title, ' +
          'certifications, tech_skills, lead_skills, available_to_mentor'
        )
        .eq('veteran_verified', true)
        .neq('id', uid)
        .not('first_name', 'is', null)
        .eq('wizard_complete', true);

      setMembers(membersData || []);
      setLoadingMembers(false);

      // Load follows I've sent
      const { data: followsData } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', uid);

      const followMap = {};
      (followsData || []).forEach(f => { followMap[f.following_id] = true; });
      setFollows(followMap);

      // Load connections (sent and received)
      const { data: connData } = await supabase
        .from('connections')
        .select('requester_id, recipient_id, status')
        .or(`requester_id.eq.${uid},recipient_id.eq.${uid}`);

      const connMap = {};
      const incoming = [];
      (connData || []).forEach(c => {
        if (c.requester_id === uid) {
          connMap[c.recipient_id] = c.status;
        } else {
          connMap[c.requester_id] = c.status;
          if (c.status === 'pending') incoming.push(c);
        }
      });
      setConnections(connMap);
      setPendingIn(incoming);
    }
    load();
  }, []);

  // ── Follow ────────────────────────────────────────────────────
  const handleFollow = async (memberId) => {
    if (!currentUserId) return;
    if (follows[memberId]) {
      // Unfollow
      await supabase.from('follows')
        .delete()
        .eq('follower_id', currentUserId)
        .eq('following_id', memberId);
      setFollows(prev => { const n = { ...prev }; delete n[memberId]; return n; });
    } else {
      // Follow
      await supabase.from('follows')
        .insert({ follower_id: currentUserId, following_id: memberId });
      setFollows(prev => ({ ...prev, [memberId]: true }));

      await supabase.from('notifications').insert({
        user_id: memberId,
        actor_id: currentUserId,
        type: 'new_follow',
        entity_id: memberId,
      });
    }
  };

  // ── Connect ───────────────────────────────────────────────────
  const handleConnect = async (memberId) => {
    if (!currentUserId) return;
    if (connections[memberId]) return; // already pending or connected
    await supabase.from('connections')
      .insert({ requester_id: currentUserId, recipient_id: memberId, status: 'pending' });
    setConnections(prev => ({ ...prev, [memberId]: 'pending' }));

    await supabase.from('notifications').insert({
      user_id: memberId,
      actor_id: currentUserId,
      type: 'connection_request',
      entity_id: memberId,
    });
  };

  // ── Accept connection ─────────────────────────────────────────
  const handleAccept = async (requesterId) => {
    if (!currentUserId) return;
    await supabase.from('connections')
      .update({ status: 'connected' })
      .eq('requester_id', requesterId)
      .eq('recipient_id', currentUserId);
    setConnections(prev => ({ ...prev, [requesterId]: 'connected' }));
    setPendingIn(prev => prev.filter(c => c.requester_id !== requesterId));

    await supabase.from('notifications').insert({
      user_id: requesterId,
      actor_id: currentUserId,
      type: 'connection_accepted',
      entity_id: currentUserId,
    });
  };

  // ── Decline connection ────────────────────────────────────────
  const handleDecline = async (requesterId) => {
    if (!currentUserId) return;
    await supabase.from('connections')
      .delete()
      .eq('requester_id', requesterId)
      .eq('recipient_id', currentUserId);
    setPendingIn(prev => prev.filter(c => c.requester_id !== requesterId));
  };

  // ── Filter ────────────────────────────────────────────────────
  const filtered = members.filter(m => {
    const fullName = `${m.first_name} ${m.last_name}`.toLowerCase();
    const allSkills = [...(m.tech_skills || []), ...(m.lead_skills || [])];
    const matchSearch = !search ||
      fullName.includes(search.toLowerCase()) ||
      (m.career_goal || '').toLowerCase().includes(search.toLowerCase()) ||
      allSkills.some(s => s.toLowerCase().includes(search.toLowerCase()));
    const matchBranch = branchFilter === 'All Branches' || m.branch === branchFilter;
    const matchSkill  = skillFilter === 'All Skills' ||
      allSkills.some(s => s.toLowerCase().includes(skillFilter.toLowerCase()));
    return matchSearch && matchBranch && matchSkill;
  });

  const mentors    = members.filter(m => m.available_to_mentor);
  const myInitials = `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`.toUpperCase() || '?';
  const connectedCount = Object.values(connections).filter(s => s === 'connected').length;

  return (
    <div className="network-root vq-surface">
      <AppNav />
      <header className="network-heading">
        <svg className="network-heading-mark" viewBox="0 0 40 48" fill="none" aria-hidden="true">
          <path d="M3 3h34v24L20 44 3 27V3Z" stroke="currentColor" strokeWidth="1.5" />
          <path d="m11 18 9-6 9 6m-18 8 9-6 9 6" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        <div>
          <p className="network-eyebrow">Veteran community</p>
          <h1>Find your people.</h1>
          <p>Connect through shared service and civilian experience.</p>
        </div>
      </header>
      <div className="network-shell">

        {/* ── MAIN COLUMN ── */}
        <main className="network-main">
          <Reveal delay={0.05}>
            <div className="network-tabs">
              {TABS.map(tab => (
                <button key={tab} className={`network-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                  {tab}
                </button>
              ))}
            </div>
          </Reveal>

          {/* ── BROWSE VETERANS ── */}
          {activeTab === 'Browse Veterans' && (
            <>
              <Reveal delay={0.1}>
                <div className="network-filters">
                  <div className="network-search-wrap">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input type="text" aria-label="Search veterans" placeholder="Search by name, goal, or skill…" value={search} onChange={e => setSearch(e.target.value)} className="network-search" />
                  </div>
                  <select className="network-select" aria-label="Filter by service branch" value={branchFilter} onChange={e => setBranchFilter(e.target.value)}>
                    {BRANCH_OPTIONS.map(b => <option key={b}>{b}</option>)}
                  </select>
                  <select className="network-select" aria-label="Filter by skill" value={skillFilter} onChange={e => setSkillFilter(e.target.value)}>
                    {SKILL_OPTIONS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </Reveal>

              {loadingMembers ? (
                <div className="network-loading" role="status">
                  <div className="network-spinner" />
                  <p>Loading veterans and their shared service backgrounds…</p>
                </div>
              ) : (
                <>
                  <div className="network-results-count">
                    {filtered.length === 0
                      ? 'No veterans match these filters yet. Try another name, branch, or skill.'
                      : <>Showing <strong>{filtered.length}</strong> veteran{filtered.length !== 1 ? 's' : ''}</>
                    }
                  </div>

                  {filtered.length === 0 && !search && branchFilter === 'All Branches' && (
                    <div className="network-empty-grid">
                      <div className="network-empty-icon network-service-mark" aria-hidden="true" />
                      <h3>The network is growing</h3>
                      <p>Veterans will appear here as members complete their profiles. Try another branch or return as the community grows.</p>
                    </div>
                  )}

                  <div className="network-grid">
                    {filtered.map((m, i) => {
                      const { color, bg } = getAvatarColor(m.id);
                      const initials = `${m.first_name?.[0] || ''}${m.last_name?.[0] || ''}`.toUpperCase();
                      const connStatus = connections[m.id];
                      const isFollowing = follows[m.id];
                      const allSkills = [...(m.tech_skills || []), ...(m.lead_skills || [])].slice(0, 3);
                      return (
                        <Reveal key={m.id} delay={Math.min(i * 0.05, 0.4)}>
                          <div className="network-member-card vq-card" data-branch={normalizeBranch(m.branch) || undefined}>
                            <div className="network-member-top">
                              {m.avatar_url
                                ? <img src={m.avatar_url} alt="avatar" className="network-member-av-img" />
                                : <div className="network-member-av" style={{ background: bg, color }}>{initials}</div>
                              }
                              <div className="network-member-info">
                                <div className="network-member-name">{m.first_name} {m.last_name}</div>
                                <div className="network-member-rank">{m.rank}</div>
                                <div className="network-member-branch">{m.branch}</div>
                              </div>
                            </div>
                            <div className="network-member-meta">
                              {m.location && (
                                <span className="network-meta-item">
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                  {m.location}
                                </span>
                              )}
                              {m.career_goal && (
                                <span className="network-meta-item">
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/></svg>
                                  {m.career_goal}
                                </span>
                              )}
                            </div>
                            {allSkills.length > 0 && (
                              <div className="network-member-skills">
                                {allSkills.map(s => <span key={s} className="network-skill-chip">{s}</span>)}
                              </div>
                            )}
                            {m.clearance && m.clearance !== 'None / Expired' && m.clearance !== 'Public Trust' && (
                              <div className="network-clearance-chip">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="10" height="10"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                                {m.clearance}
                              </div>
                            )}
                            <div className="network-member-actions">
                              <button
                                className={`network-follow-btn ${isFollowing ? 'following' : ''}`}
                                onClick={() => handleFollow(m.id)}
                              >
                                {isFollowing ? '✓ Following' : '+ Follow'}
                              </button>
                              <button
                                className={`network-connect-btn ${connStatus === 'connected' ? 'connected' : connStatus === 'pending' ? 'pending' : ''}`}
                                onClick={() => handleConnect(m.id)}
                                disabled={!!connStatus}
                              >
                                {connStatus === 'connected' ? '✓ Connected' : connStatus === 'pending' ? 'Pending' : 'Connect'}
                              </button>
                            </div>
                          </div>
                        </Reveal>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}

          {/* ── MENTOR MATCHING ── */}
          {activeTab === 'Mentor Matching' && (
            <div className="network-mentor-section">
              <Reveal delay={0.05}>
                <div className="network-mentor-banner">
                  <div className="network-mentor-banner-text">
                    <h3>Find a veteran mentor</h3>
                    <p>Learn from another veteran’s experience, ask questions, and explore paths into civilian work.</p>
                  </div>
                  <button className="network-mentor-apply-btn">Become a Mentor</button>
                </div>
              </Reveal>
              {mentors.length === 0 ? (
                <div className="network-empty-grid" style={{ marginTop: '2rem' }}>
                  <div className="network-empty-icon network-service-mark" aria-hidden="true" />
                  <h3>Mentors coming soon</h3>
                  <p>As members enable mentorship on their profiles, they'll appear here.</p>
                </div>
              ) : (
                <>
                  <div className="network-section-label">Available Mentors</div>
                  <div className="network-mentor-list">
                    {mentors.map((m, i) => {
                      const { color, bg } = getAvatarColor(m.id);
                      const initials = `${m.first_name?.[0] || ''}${m.last_name?.[0] || ''}`.toUpperCase();
                      const connStatus = connections[m.id];
                      return (
                        <Reveal key={m.id} delay={i * 0.08}>
                          <div className="network-mentor-card vq-card" data-branch={normalizeBranch(m.branch) || undefined}>
                            <div className="network-mentor-av" style={{ background: bg, color }}>{initials}</div>
                            <div className="network-mentor-details">
                              <div className="network-mentor-name">{m.first_name} {m.last_name}</div>
                              <div className="network-mentor-sub">{[m.branch?.replace('U.S. ', ''), m.location].filter(Boolean).join(' · ') || 'Service details not shared'}</div>
                              <div className="network-mentor-goal">{m.career_goal ? <>Career interest: <strong>{m.career_goal}</strong></> : 'Career interests not shared'}</div>
                            </div>
                            <div className="network-mentor-actions">
                              <button
                                className={`network-connect-btn ${connStatus === 'connected' ? 'connected' : connStatus === 'pending' ? 'pending' : ''}`}
                                onClick={() => handleConnect(m.id)}
                                disabled={!!connStatus}
                              >
                                {connStatus === 'connected' ? '✓ Connected' : connStatus === 'pending' ? 'Pending' : 'Request Mentor'}
                              </button>
                            </div>
                          </div>
                        </Reveal>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── LOCAL EVENTS ── */}
        </main>

        {/* ── LEFT SIDEBAR ── */}
        <aside className="network-left">
          <Reveal direction="left">
            <div className="network-card network-my-card vq-card vq-card--raised" data-branch={normalizeBranch(profile.branch) || undefined}>
              <div className="network-my-avatar">
                {profile.avatarUrl
                  ? <img src={profile.avatarUrl} alt="avatar" className="network-my-avatar-img" />
                  : myInitials
                }
              </div>
              <div className="network-my-name">{profile.firstName} {profile.lastName}</div>
              <div className="network-my-rank">{profile.rank || 'VetQualified Member'}</div>
              <div className="network-my-branch">{profile.branch || ''}</div>
              <div className="network-my-divider" />
              <div className="network-my-stat-row">
                <span className="network-my-stat-label">Connections</span>
                <span className="network-my-stat-val">{connectedCount}</span>
              </div>
              <div className="network-my-stat-row">
                <span className="network-my-stat-label">Profile Views</span>
                <span className="network-my-stat-val">{profile.profileViews || 0}</span>
              </div>
              <div className="network-my-stat-row">
                <span className="network-my-stat-label">Following</span>
                <span className="network-my-stat-val">{Object.keys(follows).length}</span>
              </div>
            </div>
          </Reveal>

          {/* Pending incoming requests */}
          <Reveal direction="left" delay={0.1}>
            <div className="network-card vq-card">
              <div className="network-card-title">
                Pending Requests
                {pendingIn.length > 0 && (
                  <span className="network-pending-badge">{pendingIn.length}</span>
                )}
              </div>
              {pendingIn.length === 0 ? (
                <div className="network-empty-state">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 00-3-3.87"/>
                    <path d="M16 3.13a4 4 0 010 7.75"/>
                  </svg>
                  <p>No requests yet. Browse veterans to start a connection.</p>
                </div>
              ) : (
                pendingIn.map(req => {
                  const sender = members.find(m => m.id === req.requester_id);
                  if (!sender) return null;
                  const { color, bg } = getAvatarColor(sender.id);
                  const initials = `${sender.first_name?.[0] || ''}${sender.last_name?.[0] || ''}`.toUpperCase();
                  return (
                    <div key={req.requester_id} className="network-pending-row">
                      <div className="network-suggest-av" style={{ background: bg, color }}>{initials}</div>
                      <div className="network-suggest-info">
                        <div className="network-suggest-name">{sender.first_name} {sender.last_name}</div>
                        <div className="network-suggest-role">{sender.branch?.replace('U.S. ', '')}</div>
                      </div>
                      <div className="network-pending-btns">
                        <button className="network-accept-btn" onClick={() => handleAccept(req.requester_id)}>✓</button>
                        <button className="network-decline-btn" onClick={() => handleDecline(req.requester_id)}>✕</button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Reveal>
        </aside>


      </div>
    </div>
  );
}